import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { Badge } from '@rudi/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { PageHeader } from '../components/page';
import { isMissingPracticeRoute } from '../api/hooks';
import type {
  PrescreeningCriterionAggregate,
  PrescreeningLatestRun,
  PrescreeningQuery,
  PrescreeningQueryStatus,
  PrescreeningRunSummary,
  PrescreeningRunTotals,
  PrescreeningVersionStatus,
} from '../api/types';
import { countText, dateText, practiceErrorText } from './practice-agents';

// Shared parts of the Pre-screening pages: runs and their aggregates, criteria
// queries and their versions, and each Practice's code sets.

export const PRESCREENING_UNAVAILABLE = 'This Rudi release has no pre-screening yet.';

/** Above this share of unknowns, a coded criterion likely reads the wrong codes. */
export const UNKNOWN_SHARE_LIMIT = 0.3;

const SECTIONS = [
  { to: '/prescreening', label: 'Runs', exact: true },
  { to: '/prescreening/queries', label: 'Criteria queries', exact: false },
  { to: '/prescreening/practices', label: 'Practices', exact: false },
] as const;

export function PrescreeningHeader({ actions }: { actions?: ReactNode }) {
  const tab = 'rounded-3xl border px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent';
  return (
    <>
      <PageHeader
        title="Pre-screening"
        description="The counts each run sends from a practice computer, the criteria queries behind them, and each Practice's lab code sets. Draft runs show only here."
        actions={actions}
      />
      <nav aria-label="Pre-screening" className="mb-5 flex flex-wrap gap-2">
        {SECTIONS.map(section => (
          <Link
            key={section.to}
            to={section.to}
            className={tab}
            activeOptions={{ exact: section.exact }}
            activeProps={{ className: `${tab} bg-accent font-medium text-accent-foreground` }}
          >
            {section.label}
          </Link>
        ))}
      </nav>
    </>
  );
}

/** For QueryState, which shows an Error's message. */
export function prescreeningLoadError(error: unknown): Error | null {
  if (!error) return null;
  if (isMissingPracticeRoute(error)) return new Error(PRESCREENING_UNAVAILABLE);
  return new Error(practiceErrorText(error, 'Could not load pre-screening'));
}

/** Rudi's own 404 names the record; FastAPI's 422 means the ID is not one. */
export function isUnknownRecord(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { error?: unknown } } } | null)
    ?.response;
  return (
    response?.status === 422 ||
    (response?.status === 404 && typeof response.data?.error === 'string')
  );
}

/** The reasons Rudi gave for refusing a document, if it listed any. */
export function rudiErrorList(error: unknown): string[] {
  const details = (error as { response?: { data?: { details?: { errors?: unknown } } } })?.response
    ?.data?.details;
  return Array.isArray(details?.errors)
    ? details.errors.filter((item): item is string => typeof item === 'string')
    : [];
}

export const QUERY_STATUS_TEXT: Record<PrescreeningQueryStatus, string> = {
  designing: 'Designing',
  awaiting_approval: 'Awaiting approval',
  approved: 'Approved',
  rejected: 'Rejected',
};

export function QueryStatusBadge({ status }: { status: PrescreeningQueryStatus }) {
  const variant =
    status === 'approved'
      ? 'default'
      : status === 'awaiting_approval'
        ? 'secondary'
        : status === 'rejected'
          ? 'destructive'
          : 'outline';
  return <Badge variant={variant}>{QUERY_STATUS_TEXT[status] ?? status}</Badge>;
}

export const VERSION_STATUS_TEXT: Record<PrescreeningVersionStatus, string> = {
  draft: 'Draft',
  awaiting_approval: 'Awaiting approval',
  approved: 'Approved',
  superseded: 'Superseded',
  rejected: 'Rejected',
};

export function VersionStatusBadge({ status }: { status: PrescreeningVersionStatus }) {
  const variant =
    status === 'approved'
      ? 'default'
      : status === 'awaiting_approval'
        ? 'secondary'
        : status === 'rejected'
          ? 'destructive'
          : 'outline';
  return <Badge variant={variant}>{VERSION_STATUS_TEXT[status] ?? status}</Badge>;
}

export function DraftBadge() {
  return (
    <Badge variant="outline" className="border-dashed">
      Draft
    </Badge>
  );
}

const TRIGGER_TEXT: Record<string, string> = {
  import: 'After an import',
  query: 'New version',
  bundle: 'New engine bundle',
  job: 'Job from Cortex',
  manual: 'Started at the practice',
};

export function triggerText(trigger: string): string {
  return TRIGGER_TEXT[trigger] ?? trigger;
}

export function shareText(share: number | null | undefined): string {
  return typeof share === 'number' ? `${Math.round(share * 100)}%` : 'n/a';
}

/**
 * A coded criterion whose share of unknowns is high. Stage 3 criteria stay
 * unknown until the patient model reads them, so they never count.
 */
export function isHighUnknownShare(criterion: PrescreeningCriterionAggregate): boolean {
  return (
    criterion.stage !== 3 &&
    typeof criterion.unknownShare === 'number' &&
    criterion.unknownShare > UNKNOWN_SHARE_LIMIT
  );
}

/** One line of run totals, with "fewer than 5" for small counts. */
export function totalsText(totals: PrescreeningRunTotals | PrescreeningLatestRun): string {
  return `${countText(totals.candidates)} candidates, ${countText(totals.needsReview)} need review, ${countText(totals.excluded)} excluded, of ${countText(totals.screened)} screened`;
}

export function practiceName(name: string | null | undefined): string {
  return name || 'Unnamed Practice';
}

export function trialName(item: {
  title?: string | null;
  shortTitle?: string | null;
  catalogId?: string | null;
}): string {
  return item.shortTitle || item.title || item.catalogId || 'Unknown trial';
}

/** Where a query or run gets its patients: a Practice, or Rudi patient lists. */
export function SourceCell({
  source,
  practiceId,
  name,
}: {
  source: string;
  practiceId?: string | null;
  name?: string | null;
}) {
  if (source === 'patient_lists') return <span>Patient lists in Rudi</span>;
  if (!practiceId) return <span>{practiceName(name)}</span>;
  return (
    <Link
      to="/prescreening/practices/$practiceId"
      params={{ practiceId }}
      className="underline-offset-4 hover:underline"
    >
      {practiceName(name)}
    </Link>
  );
}

/** Opens the criterion in a new version of the query, in the editor on the query page. */
export function MarkAsWrongLink({
  queryId,
  version,
  criterionId,
}: {
  queryId: string;
  version: number;
  criterionId: string;
}) {
  return (
    <Link
      to="/prescreening/queries/$queryId"
      params={{ queryId }}
      search={{ edit: version, criterion: criterionId }}
      className="whitespace-nowrap text-xs underline-offset-4 hover:underline"
      aria-label={`Mark ${criterionId} as wrong`}
    >
      Mark as wrong
    </Link>
  );
}

export function RunsTable({
  runs,
  showSource = true,
  empty,
}: {
  runs: PrescreeningRunSummary[];
  showSource?: boolean;
  empty: string;
}) {
  const columns = showSource ? 10 : 9;
  return (
    <div className="overflow-x-auto rounded-3xl border">
      <Table aria-label="Runs">
        <TableHeader>
          <TableRow>
            <TableHead>Finished</TableHead>
            {showSource && <TableHead>Practice</TableHead>}
            <TableHead>Trial</TableHead>
            <TableHead>Query version</TableHead>
            <TableHead>Bundle</TableHead>
            <TableHead>Trigger</TableHead>
            <TableHead className="text-right">Screened</TableHead>
            <TableHead className="text-right">Candidates</TableHead>
            <TableHead className="text-right">Needs review</TableHead>
            <TableHead className="text-right">Excluded</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {runs.map(run => (
            <TableRow key={run.id}>
              <TableCell className="min-w-[10rem]">
                <Link
                  to="/prescreening/runs/$runId"
                  params={{ runId: run.id }}
                  className="font-medium underline-offset-4 hover:underline"
                >
                  {dateText(run.finishedAt ?? run.receivedAt)}
                </Link>
              </TableCell>
              {showSource && (
                <TableCell className="min-w-[10rem]">
                  <SourceCell
                    source={run.source}
                    practiceId={run.practiceId}
                    name={run.practiceName}
                  />
                </TableCell>
              )}
              <TableCell className="min-w-[12rem]">
                <div>{trialName(run)}</div>
                {run.catalogId && run.title && (
                  <div className="text-xs text-muted-foreground">{run.catalogId}</div>
                )}
              </TableCell>
              <TableCell className="min-w-[8rem]">
                <Link
                  to="/prescreening/queries/$queryId"
                  params={{ queryId: run.queryId }}
                  className="underline-offset-4 hover:underline"
                >
                  Version {run.version}
                </Link>
                {run.draft && (
                  <span className="ml-2">
                    <DraftBadge />
                  </span>
                )}
              </TableCell>
              <TableCell className="text-xs">{run.bundleVersion ?? 'Unknown'}</TableCell>
              <TableCell className="text-xs">{triggerText(run.trigger)}</TableCell>
              <TableCell className="text-right">{countText(run.totals.screened)}</TableCell>
              <TableCell className="text-right">{countText(run.totals.candidates)}</TableCell>
              <TableCell className="text-right">{countText(run.totals.needsReview)}</TableCell>
              <TableCell className="text-right">{countText(run.totals.excluded)}</TableCell>
            </TableRow>
          ))}
          {runs.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns} className="h-24 text-center text-muted-foreground">
                {empty}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function LatestRunText({ run }: { run: PrescreeningLatestRun | null | undefined }) {
  if (!run?.runId) return <span className="text-muted-foreground">No run yet</span>;
  return (
    <div className="text-xs">
      <Link
        to="/prescreening/runs/$runId"
        params={{ runId: run.runId }}
        className="underline-offset-4 hover:underline"
      >
        {run.version ? `Version ${run.version}, ` : ''}
        {dateText(run.finishedAt)}
      </Link>
      <div className="text-muted-foreground">{totalsText(run)}</div>
    </div>
  );
}

export function newestVersion(query: PrescreeningQuery) {
  return query.versions.length ? query.versions[query.versions.length - 1] : undefined;
}

export function QueriesTable({
  queries,
  showSource = true,
  empty,
}: {
  queries: PrescreeningQuery[];
  showSource?: boolean;
  empty: string;
}) {
  const columns = showSource ? 6 : 5;
  return (
    <div className="overflow-x-auto rounded-3xl border">
      <Table aria-label="Criteria queries">
        <TableHeader>
          <TableRow>
            <TableHead>Trial</TableHead>
            {showSource && <TableHead>Practice</TableHead>}
            <TableHead>Status</TableHead>
            <TableHead>Versions</TableHead>
            <TableHead>Latest draft run</TableHead>
            <TableHead>Latest approved run</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {queries.map(query => {
            const newest = newestVersion(query);
            return (
              <TableRow key={query.queryId}>
                <TableCell className="min-w-[14rem]">
                  <Link
                    to="/prescreening/queries/$queryId"
                    params={{ queryId: query.queryId }}
                    className="font-medium underline-offset-4 hover:underline"
                  >
                    {trialName(query)}
                  </Link>
                  <div className="text-xs text-muted-foreground">{query.catalogId}</div>
                </TableCell>
                {showSource && (
                  <TableCell className="min-w-[10rem]">
                    <SourceCell
                      source={query.source}
                      practiceId={query.practiceId}
                      name={query.practiceName}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <QueryStatusBadge status={query.status} />
                </TableCell>
                <TableCell className="min-w-[9rem] text-xs">
                  <div>
                    {query.activeVersion ? `Version ${query.activeVersion} active` : 'None active'}
                  </div>
                  {newest && newest.version !== query.activeVersion && (
                    <div className="text-muted-foreground">
                      Newest {newest.version}, {VERSION_STATUS_TEXT[newest.status] ?? newest.status}
                    </div>
                  )}
                </TableCell>
                <TableCell className="min-w-[12rem]">
                  <LatestRunText run={query.latestDraftRun} />
                </TableCell>
                <TableCell className="min-w-[12rem]">
                  <LatestRunText run={query.latestRun} />
                </TableCell>
              </TableRow>
            );
          })}
          {queries.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns} className="h-24 text-center text-muted-foreground">
                {empty}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
